// Modal de divisão de pedido grande em 2/3/4 filhos.
// Gestor decide:
//   • Quantas partes (mínimo 2, máximo 4 no MVP)
//   • Em qual parte cada móvel vai (dropdown por móvel)
//   • Data prevista de cada parte
//   • Observação opcional por parte
//
// Restrições visuais:
//   • Cada parte precisa ter pelo menos 1 móvel
//   • Datas obrigatórias

import { useState, useMemo } from 'react'
import { Split, Check, X, Calendar, Package } from 'lucide-react'
import { hojeYmd } from '../data/ajustes'
import { descricaoMovel } from '../utils/fmt'

const LETRAS = ['A', 'B', 'C', 'D']
const CORES_PARTE = {
  A: { bg: '#A5B4FC', text: '#1E1B4B' },
  B: { bg: '#FCA5A5', text: '#7F1D1D' },
  C: { bg: '#86EFAC', text: '#14532D' },
  D: { bg: '#FCD34D', text: '#78350F' },
}

export function DividirPedidoModal({ pedido, onClose, onConfirm }) {
  const moveis = pedido?.moveis || []
  const [numPartes, setNumPartes] = useState(2)

  // Mapa: moveisId → letra da parte. Inicial: tudo na parte A.
  const [alocacao, setAlocacao] = useState(() => {
    const init = {}
    moveis.forEach(m => { init[m.id] = 'A' })
    return init
  })

  // Configurações por parte (data + observação)
  const [partesConfig, setPartesConfig] = useState(() => ({
    A: { prazo: pedido?.prazo || hojeYmd(), observacao: '' },
    B: { prazo: pedido?.prazo || hojeYmd(), observacao: '' },
    C: { prazo: pedido?.prazo || hojeYmd(), observacao: '' },
    D: { prazo: pedido?.prazo || hojeYmd(), observacao: '' },
  }))

  // Quando o número de partes muda, móveis em letras fora do range voltam pra A.
  function setNumPartesSafe(n) {
    setNumPartes(n)
    const letrasValidas = new Set(LETRAS.slice(0, n))
    setAlocacao(prev => {
      const novo = { ...prev }
      Object.keys(novo).forEach(id => {
        if (!letrasValidas.has(novo[id])) novo[id] = 'A'
      })
      return novo
    })
  }

  // Conta móveis por parte
  const contagem = useMemo(() => {
    const c = { A: 0, B: 0, C: 0, D: 0 }
    Object.values(alocacao).forEach(l => { c[l] = (c[l] || 0) + 1 })
    return c
  }, [alocacao])

  const letrasAtivas = LETRAS.slice(0, numPartes)
  const todasComMoveis = letrasAtivas.every(l => contagem[l] > 0)
  const todasComPrazo  = letrasAtivas.every(l => !!partesConfig[l].prazo)
  const podeConfirmar  = todasComMoveis && todasComPrazo

  function confirmar() {
    if (!podeConfirmar) return
    const partes = letrasAtivas.map(letra => {
      const moveisDaParte = moveis.filter(m => alocacao[m.id] === letra)
      return {
        moveis:     moveisDaParte,
        prazo:      partesConfig[letra].prazo,
        observacao: partesConfig[letra].observacao,
      }
    })
    onConfirm(partes)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Split size={16} className="text-blue-600" />
            Dividir pedido em partes
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          <p className="text-sm text-slate-500">
            Cria pedidos filhos (<strong>{pedido?.numero}/A, /B, ...</strong>) com subset
            de móveis e data própria. O pedido <strong>mãe</strong> guarda o valor financeiro
            e sai da Logística. Métricas não duplicam.
          </p>

          {/* Quantas partes */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-2">
              Número de partes
            </label>
            <div className="flex gap-2">
              {[2, 3, 4].map(n => (
                <button
                  key={n}
                  onClick={() => setNumPartesSafe(n)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors
                    ${numPartes === n
                      ? 'bg-[#111] text-[#F5C200]'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                >
                  {n} partes
                </button>
              ))}
            </div>
          </div>

          {/* Configuração de cada parte */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {letrasAtivas.map(letra => {
              const c = CORES_PARTE[letra]
              return (
                <div
                  key={letra}
                  className="rounded-xl border-2 p-3"
                  style={{ borderColor: c.bg + '80', backgroundColor: c.bg + '10' }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-black"
                      style={{ backgroundColor: c.bg, color: c.text }}
                    >
                      {letra}
                    </span>
                    <span className="text-sm font-bold text-slate-800">
                      Parte {letra}
                    </span>
                    <span className="ml-auto text-[10px] font-bold bg-white/70 text-slate-600 px-2 py-0.5 rounded-full">
                      <Package size={9} className="inline mr-1" />
                      {contagem[letra]} móve{contagem[letra] === 1 ? 'l' : 'is'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">
                        <Calendar size={9} className="inline mr-1" />
                        Prazo
                      </label>
                      <input
                        type="date"
                        value={partesConfig[letra].prazo}
                        onChange={e => setPartesConfig(prev => ({
                          ...prev,
                          [letra]: { ...prev[letra], prazo: e.target.value },
                        }))}
                        className="input w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">
                        Observação
                      </label>
                      <input
                        type="text"
                        value={partesConfig[letra].observacao}
                        onChange={e => setPartesConfig(prev => ({
                          ...prev,
                          [letra]: { ...prev[letra], observacao: e.target.value },
                        }))}
                        placeholder="Ex: leva sala primeiro"
                        className="input w-full text-sm"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Alocação móvel por móvel */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-2">
              Distribuir móveis ({moveis.length} total)
            </label>
            <div className="space-y-1.5">
              {moveis.map((m, i) => (
                <div key={m.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                  <span className="w-6 h-6 rounded bg-white text-slate-500 flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </span>
                  <p className="flex-1 text-xs text-slate-700 leading-snug">{descricaoMovel(m)}</p>
                  <div className="flex gap-1 shrink-0">
                    {letrasAtivas.map(letra => {
                      const c = CORES_PARTE[letra]
                      const ativo = alocacao[m.id] === letra
                      return (
                        <button
                          key={letra}
                          onClick={() => setAlocacao(prev => ({ ...prev, [m.id]: letra }))}
                          className="w-7 h-7 rounded text-xs font-black transition-all"
                          style={ativo
                            ? { backgroundColor: c.bg, color: c.text }
                            : { backgroundColor: '#fff', color: '#94A3B8', border: '1.5px solid #E2E8F0' }}
                        >
                          {letra}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!todasComMoveis && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              ⚠ Cada parte precisa ter pelo menos 1 móvel
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="btn-secondary text-sm">
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={!podeConfirmar}
            className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check size={14} />
            Dividir em {numPartes} partes
          </button>
        </div>
      </div>
    </div>
  )
}
